// Section 11 schooling and the Section 18 schooling and school-subjects policies.
// Children 6 to 14 who are not working attend while the policy is not `none`, up to the school
// seats (12 per school, scaled by condition) and each teacher's output (6 students per fully
// productive teacher, Section 8). Each student
// gains 0.25 per season (the `skillGain` target via `school`) in every policy subject their
// teacher can teach, capped at the teacher's own level; Literacy counts seasons towards the
// `literate` flag (8). Compulsory options pull children of that age from work (the `schooling`
// work block keeps them out of production; here their job is cleared). D-30.
import { effectiveCount, isChild, peopleSorted } from '../../core/queries';
import type {
  Person,
  PersonId,
  PhaseContext,
  ReadContext,
  SchoolSubject,
  SkillId,
} from '../../core/types';
import { workBlock } from '../shared/eligibility';
import { num } from '../shared/value';

export interface Teachable {
  subject: SchoolSubject;
  /** The highest level a student can reach from this teacher (Section 11). */
  cap: number;
}

/**
 * What a teacher can teach of the policy's subjects: a subject where they have skill 2+ (capped
 * at their skill); any subject up to skill 2 if literate; Literacy always (D-30).
 */
export function teachable(ctx: ReadContext, teacher: Person): Teachable[] {
  const s = ctx.cfg.skills;
  const out: Teachable[] = [];
  for (const subject of ctx.state.policies.schoolSubjects) {
    if (subject === 'literacy') {
      out.push({ subject, cap: s.max });
      continue;
    }
    const own = teacher.skills[subject] ?? 0;
    if (own >= s.teacherMinSkill) out.push({ subject, cap: own });
    else if (teacher.status.literate) out.push({ subject, cap: s.literateTeachLevel });
  }
  return out;
}

export interface SchoolRoster {
  seats: number;
  /** Teachers who taught this season (worked, with at least one teachable subject). */
  teachers: { id: PersonId; subjects: Teachable[] }[];
  /** Student -> teacher. */
  students: Record<PersonId, PersonId>;
  /** Children who would attend but found no seat or teacher. */
  waiting: PersonId[];
}

function schoolAge(ctx: ReadContext, p: Person): boolean {
  return (
    isChild(ctx.cfg, p) &&
    p.ageSeasons >= ctx.cfg.skills.schoolAgeFromYears * ctx.cfg.people.seasonsPerYear
  );
}

/** Is this child free to attend school this season (not working, present and well)? */
function canAttend(ctx: ReadContext, p: Person): boolean {
  if (!schoolAge(ctx, p)) return false;
  if (p.status.sick !== null || p.status.injured !== null || p.status.inCustody !== null)
    return false;
  // Children with a job attend only if they cannot work it (compulsory schooling, child labour).
  return p.job === 'rest' || workBlock(ctx, p, p.job) !== null;
}

/** Section 11: who sits with which teacher this season. Pure over the committed state. */
export function schoolRoster(ctx: ReadContext): SchoolRoster {
  const empty: SchoolRoster = { seats: 0, teachers: [], students: {}, waiting: [] };
  const state = ctx.state;
  if (state.policies.schooling === 'none') return empty;
  const people = peopleSorted(state);
  const seats = Math.floor(
    ctx.cfg.buildings.school.seats * effectiveCount(ctx.cfg, state, 'school') + 1e-9,
  );
  const outputs = state.currentReport?.outputs ?? {};
  const teachers = people
    .filter((p) => p.job === 'teacher' && (outputs[p.id]?.amount ?? 0) > 0)
    .map((p) => ({ id: p.id, subjects: teachable(ctx, p) }))
    .filter((t) => t.subjects.length > 0);
  // Each teacher seats floor(output) students (6 per fully productive teacher, Section 8).
  const places = (id: PersonId): number => Math.floor((outputs[id]?.amount ?? 0) + 1e-9);
  const load = new Map<PersonId, number>();
  const students: Record<PersonId, PersonId> = {};
  const waiting: PersonId[] = [];
  let seated = 0;
  for (const child of people) {
    if (!canAttend(ctx, child)) continue;
    const teacher = teachers.find((t) => (load.get(t.id) ?? 0) < places(t.id));
    if (teacher === undefined || seated >= seats) {
      waiting.push(child.id);
      continue;
    }
    students[child.id] = teacher.id;
    load.set(teacher.id, (load.get(teacher.id) ?? 0) + 1);
    seated++;
  }
  return { seats, teachers, students, waiting };
}

/** Compulsory schooling: children of that age leave their jobs (the slot is left empty). */
export function pullFromWork(ctx: PhaseContext): void {
  for (const snap of peopleSorted(ctx.state)) {
    if (!isChild(ctx.cfg, snap) || snap.job === 'rest') continue;
    if (workBlock(ctx, snap, snap.job) !== 'schooling') continue;
    const p = ctx.draft.people[snap.id];
    if (p === undefined) continue;
    ctx.report.add({ kind: 'job-vacant', jobId: p.job, personId: p.id, reason: 'unable' });
    p.job = 'rest';
    p.streaks.inJob = 0;
  }
}

function learn(
  ctx: PhaseContext,
  p: Person,
  skill: SkillId,
  teacherId: PersonId,
  cap: number,
): boolean {
  const before = p.skills[skill] ?? 0;
  p.skillLastUsed[skill] = ctx.season.turn;
  if (before >= cap) return false;
  const gain = Math.max(
    0,
    num(ctx, { kind: 'skillGain', personId: p.id, skill, via: 'school', teacherId }),
  );
  const after = Math.min(cap, ctx.cfg.skills.max, before + gain);
  if (after <= before) return false;
  p.skills[skill] = after;
  if (after > (p.skillPeaks[skill] ?? 0)) p.skillPeaks[skill] = after;
  if (Math.floor(after) > Math.floor(before))
    ctx.report.add({ kind: 'skill', personId: p.id, skill, level: Math.floor(after) });
  return true;
}

/** Section 11: seat students, teach them and set `inSchool`; returns the roster. */
export function runSchool(ctx: PhaseContext): SchoolRoster {
  const roster = schoolRoster(ctx);
  const taught = new Map<PersonId, Set<SkillId>>();
  const subjectsTaught = new Set<SchoolSubject>();
  const byTeacher = new Map(roster.teachers.map((t) => [t.id, t.subjects]));
  for (const id of Object.keys(roster.students).sort() as PersonId[]) {
    const teacherId = roster.students[id];
    const p = ctx.draft.people[id];
    const subjects = teacherId === undefined ? undefined : byTeacher.get(teacherId);
    if (p === undefined || teacherId === undefined || subjects === undefined) continue;
    for (const { subject, cap } of subjects) {
      subjectsTaught.add(subject);
      if (subject === 'literacy') {
        p.streaks.school += 1;
        if (!p.status.literate && p.streaks.school >= ctx.cfg.skills.literateAfterSchoolSeasons) {
          p.status.literate = true;
          ctx.report.add({ kind: 'literate', personId: p.id });
        }
        continue;
      }
      learn(ctx, p, subject, teacherId, cap);
      const set = taught.get(teacherId) ?? new Set<SkillId>();
      set.add(subject);
      taught.set(teacherId, set);
    }
  }
  // Teaching a subject keeps the teacher's own skill in use (Section 11 decay).
  for (const [teacherId, skills] of taught) {
    const t = ctx.draft.people[teacherId];
    if (t !== undefined) for (const skill of skills) t.skillLastUsed[skill] = ctx.season.turn;
  }
  // `inSchool` follows this season's roster; draft only on change.
  for (const snap of peopleSorted(ctx.state)) {
    const seated = roster.students[snap.id] !== undefined;
    if (snap.status.inSchool === seated) continue;
    const p = ctx.draft.people[snap.id];
    if (p !== undefined) p.status.inSchool = seated;
  }
  const students = Object.keys(roster.students).length;
  if (students > 0 || (roster.seats > 0 && roster.waiting.length > 0)) {
    ctx.report.add({
      kind: 'school',
      students,
      teachers: roster.teachers.length,
      seats: roster.seats,
      waiting: roster.waiting.length,
      subjects: [...subjectsTaught].sort(),
    });
  }
  return roster;
}
